import WorkspacePrimaryButton from "@/components/PrimaryButton";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { WorkspaceBrand } from "@/components/workspace/WorkspaceShell";
import "@/components/workspace/workspace.css";
import TermsAndConditions from "@/components/TermsConditions/TermsConditions";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import { useAuthStore } from "@/store/useAuthStore";
import GoogleLoginButton from "@/components/GoogleLogin";
import ClassLinkLoginButton from "@/components/ClassLinkLogin";
import CleverLoginButton from "@/components/CleverLogin";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import { identifyMixpanelUser } from "@/utils/mixpanel-utils";
import { authClient } from "@/lib/auth-client";
import LoadingSpinner from "@/components/LoadingSpinner";
import { isTeacher } from "@/utils/role-utils";
import { safeEmitFrontendCustomerIOEvent } from "@/services/customerioEvents";
import { setSupportSession } from "@/utils/supportSession";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [error, setError] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();
  const { user, isAuthenticated, setAuth } = useAuthStore();

  // On mount, directly check for valid session via getSession and redirect
  useEffect(() => {
    const checkSessionAndRedirect = async () => {
      try {
        const { data } = await authClient.getSession();
        if (data?.user) {
          const role =
            (data.user as any).account_role ||
            (data.user as any).role ||
            "STUDENT";
          // Sync to auth store before redirecting
          const parsedUserId = Number.parseInt(data.user.id, 10);
          if (!Number.isNaN(parsedUserId)) {
            setAuth(data.session?.token || "", {
              id: parsedUserId,
              username: (data.user as any).username || "",
              displayUsername: (data.user as any).displayUsername,
              email: data.user.email,
              name: data.user.name || "",
              role: role,
              account_type: (data.user as any).account_type,
              picture: data.user.image || undefined,
              classrooms: [],
            });
          }
          // Redirect based on role
          if (isTeacher(role)) {
            navigate("/lessons", { replace: true });
          } else {
            navigate("/classrooms", { replace: true });
          }
          return;
        }
      } catch (error) {
        console.error("Session check error:", error);
      }
      setIsCheckingSession(false);
    };
    checkSessionAndRedirect();
  }, [navigate, setAuth]);

  // Also handle store-based auth state changes (for credential login)
  useEffect(() => {
    if (!isCheckingSession && isAuthenticated && user) {
      if (isTeacher(user.role)) {
        navigate("/lessons", { replace: true });
      } else {
        navigate("/classrooms", { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, isCheckingSession]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget as HTMLFormElement);
    const username = formData.get("username") as string;
    const password = formData.get("password") as string;

    // Validate form completeness
    if (!username.trim() || !password.trim()) {
      showNotification(
        "Username and password are required",
        NotificationType.WARNING,
      );
      setIsLoading(false);
      return;
    }

    if (!isChecked) {
      showNotification(
        "Please agree to the terms and conditions",
        NotificationType.WARNING,
      );
      setIsLoading(false);
      return;
    }

    try {
      // Use BetterAuth for authentication
      const { data, error } = await authClient.signIn.username({
        username, // Use username directly
        password,
        callbackURL: "/",
      });

      if (error) {
        console.error("Login error:", error);
        // Handle email verification required (403)
        if (error.status === 403) {
          setError("Please verify your email address before logging in.");
          showNotification(
            "Please verify your email address. Check your inbox for the verification link.",
            NotificationType.WARNING,
          );
          return;
        }
        setError(error.message || "Invalid username or password");
        showNotification(
          error.message || "Invalid username or password. Please try again.",
          NotificationType.WARNING,
        );
        return;
      }

      if (!data?.user) {
        showNotification(
          "Invalid username or password. Please try again.",
          NotificationType.WARNING,
        );
        setIsLoading(false);
        return;
      }

      const user = data.user;
      const role =
        (user as any).account_role || (user as any).role || "STUDENT";

      const parsedUserId = Number.parseInt(user.id, 10);
      if (Number.isNaN(parsedUserId)) {
        console.error("Login error: invalid user id", user.id);
        showNotification(
          "Invalid user data received. Please try again.",
          NotificationType.WARNING,
        );
        setIsLoading(false);
        return;
      }

      // Update auth store with the session
      // A credential sign-in is never a support session: drop any marker
      // left by a previous one before this identity is established.
      setSupportSession(null);
      setAuth(data.token || "", {
        id: parsedUserId,
        username: user.username || "",
        displayUsername: user.displayUsername || undefined,
        email: user.email,
        name: user.name || "",
        role,
        account_type: (user as any).account_type,
        picture: user.image || undefined,
        classrooms: [],
      });

      identifyMixpanelUser(user.id, {
        $email: user.email,
        $name: user.name,
        username: user.username,
        role,
        login_type: "credentials",
      });
      const loginAt = new Date().toISOString();
      void safeEmitFrontendCustomerIOEvent({
        event_name: "login",
        occurred_at: loginAt,
        properties: {
          login_at: loginAt,
          login_type: "credentials",
          role,
        },
      });

      // Redirect based on user role
      if (isTeacher(role)) {
        navigate("/lessons");
      } else {
        navigate("/classrooms");
      }
    } catch (err: any) {
      console.error("Login error:", err);
      setError("Incorrect username or password.");
      showNotification(
        "Invalid username or password. Please try again.",
        NotificationType.WARNING,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking for existing session (SSO callback)
  if (isCheckingSession) {
    return (
      <LoadingSpinner message="Checking authentication..." placement="screen" />
    );
  }

  return (
    <div className="et-auth et-login">
      <section className="et-auth-form-side">
        <WorkspaceBrand />
        <div className="et-auth-form">
          <h1>
            Hey amazing
            <br />
            <em>Educators and Students!</em>
          </h1>
          <p className="et-auth-social-label">Login with</p>
          <div className="et-auth-social">
            <GoogleLoginButton label="Google" />
            <CleverLoginButton />
            <ClassLinkLoginButton />
          </div>
          <div className="et-auth-divider">or</div>
          <form onSubmit={handleSubmit}>
            <div className="et-auth-fields">
              <label>
                <span className="sr-only">Username</span>
                <input
                  name="username"
                  autoComplete="username"
                  placeholder="Enter your username"
                  required
                />
              </label>
              <div className="et-auth-password">
                <label htmlFor="login-password" className="sr-only">
                  Password
                </label>
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="et-icon-button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="button"
              className="et-auth-forgot"
              onClick={() => setIsForgotPasswordOpen(true)}
            >
              Forgot password?
            </button>
            {error && (
              <p className="et-auth-error" role="alert">
                {error}
              </p>
            )}
            <div className="et-auth-agreement">
              <TermsAndConditions
                isChecked={isChecked}
                setIsChecked={setIsChecked}
              />
            </div>
            <WorkspacePrimaryButton
              type="submit"
              className="et-primary-button et-auth-submit"
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? "Verifying…" : "Login"}
            </WorkspacePrimaryButton>
          </form>
          <p className="et-auth-switch">
            Don't have an account? <a href="/signup">Sign up here</a>
          </p>
        </div>
      </section>
      <aside className="et-auth-art">
        <img src="/design/library.webp" alt="" loading="lazy" />
        <p>
          Bring your lesson to life in minutes for a{" "}
          <em>personalized engaging 1:1 tutoring experience.</em>
        </p>
      </aside>
      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
        debugMode={false}
      />
    </div>
  );
};

export default Login;
